/**
 * Site QA run loop: approved boxes → run → fail PNG + path → Grok window
 * (screenshot path + job brief) → wait → hard reset → recheck.
 * Stops when the approved checklist is complete.
 */

import { generateRunTasks } from "./generate";
import { checkOff, checklistComplete, failPost } from "./protocol";
import {
  hardResetSession,
  openGrokWindow,
  runSidecarTask,
  startBrowserSession,
  stopBrowserSession,
  waitGrokJob,
} from "./sidecar";
import { QA_MAX_RETRIES } from "./types";
import type { QaItem, QaSession, QaTask } from "./types";

export type QaLoopCallbacks = {
  onSession: (s: QaSession) => void;
  onStatus: (msg: string) => void;
  /** Fail inbox: screenshot path only. */
  onFailPath: (path: string) => void;
  shouldStop: () => boolean;
};

function patchItem(session: QaSession, id: string, patch: Partial<QaItem>): QaSession {
  return {
    ...session,
    items: session.items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
  };
}

function emit(cb: QaLoopCallbacks, session: QaSession): QaSession {
  cb.onSession(session);
  return session;
}

async function ensureBrowser(session: QaSession): Promise<QaSession> {
  if (session.sidecarSessionId) return session;
  const { sessionId } = await startBrowserSession(session.targetUrl, session.repoPath);
  return { ...session, sidecarSessionId: sessionId };
}

export async function runQaLoop(
  start: QaSession,
  cb: QaLoopCallbacks,
): Promise<QaSession> {
  let session = start.tasks.length ? start : generateRunTasks(start);
  session = emit(cb, { ...session, running: true });

  if (!session.tasks.length) {
    cb.onStatus("Generate run tasks first — no approved Grok boxes");
    return emit(cb, { ...session, running: false });
  }

  try {
    session = await ensureBrowser(session);
    session = emit(cb, session);
  } catch (e) {
    cb.onStatus(
      e instanceof Error
        ? e.message
        : "Sidecar not running. From AgentForge root: npm run qa:sidecar",
    );
    return emit(cb, { ...session, running: false });
  }

  const queue: QaTask[] = session.tasks.filter((t) => {
    const item = session.items.find((i) => i.id === t.itemId);
    return item && item.status !== "pass";
  });

  for (const task of queue) {
    if (cb.shouldStop()) break;
    session = await runOneBox(session, task, cb);
    if (checklistComplete(session.items)) break;
  }

  const done = checklistComplete(session.items);
  cb.onStatus(done ? "Checklist complete" : cb.shouldStop() ? "Stopped" : "Run finished");
  if (session.sidecarSessionId && (done || cb.shouldStop())) {
    try {
      await stopBrowserSession(session.sidecarSessionId);
    } catch {
      /* keep session for a retry */
    }
    if (done || cb.shouldStop()) session = { ...session, sidecarSessionId: undefined };
  }
  return emit(cb, { ...session, running: false });
}

async function runOneBox(
  session: QaSession,
  task: QaTask,
  cb: QaLoopCallbacks,
): Promise<QaSession> {
  const item = session.items.find((i) => i.id === task.itemId);
  if (!item || item.status === "pass") return session;

  let current = session;
  while (!cb.shouldStop()) {
    const box = current.items.find((i) => i.id === task.itemId);
    if (!box || box.status === "pass") return current;
    if (box.retries >= QA_MAX_RETRIES) {
      cb.onStatus(`Gave up on ${box.title} after ${QA_MAX_RETRIES} retries`);
      return current;
    }

    current = emit(cb, patchItem(current, task.itemId, { status: "running" }));
    cb.onStatus(`Running: ${task.title}`);

    try {
      if (current.sidecarSessionId) {
        await hardResetSession(current.sidecarSessionId, current.targetUrl);
      }
    } catch (e) {
      cb.onStatus(e instanceof Error ? e.message : "Hard reset failed");
      return current;
    }

    if (cb.shouldStop()) return current;

    let result: {
      ok: boolean;
      screenshotPath?: string;
      error?: string;
      pageUrl?: string;
    };
    try {
      result = await runSidecarTask({
        sessionId: current.sidecarSessionId || "",
        itemId: task.itemId,
        title: task.title,
        actions: task.actions,
      });
    } catch (e) {
      result = { ok: false, error: e instanceof Error ? e.message : String(e) };
    }

    if (cb.shouldStop()) return current;

    if (result.ok) {
      const latest = current.items.find((i) => i.id === task.itemId);
      if (!latest) return current;
      const off = checkOff(latest);
      if (!off.ok) {
        cb.onStatus(off.reason);
        return current;
      }
      return emit(cb, patchItem(current, task.itemId, off.item));
    }

    const path = failPost(result.screenshotPath || "");
    current = emit(
      cb,
      patchItem(current, task.itemId, {
        status: "waiting-fix",
        screenshotPath: path || undefined,
        lastFailAt: new Date().toISOString(),
        retries: box.retries + 1,
      }),
    );
    if (path) cb.onFailPath(path);

    if (!path) {
      cb.onStatus(result.error || "Failed with no screenshot path");
      return current;
    }

    cb.onStatus("Waiting on Grok window");
    try {
      const job = await openGrokWindow({
        screenshotPath: path,
        repoPath: current.repoPath.trim(),
        itemId: task.itemId,
        title: task.title,
        error: result.error || "",
        targetUrl: current.targetUrl,
        pageUrl: result.pageUrl,
        actions: task.actions,
      });
      await waitGrokJob(job.jobId, cb.shouldStop);
    } catch (e) {
      cb.onStatus(e instanceof Error ? e.message : "Could not open Grok window");
      return current;
    }
  }
  return current;
}
